import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { Bed, Edit2, Eye, Loader2, Plus, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchPatient } from '../hooks/useSearchPatient'
import { Patient } from '../types/patient'
import { PatientDetailDialog } from './PatientDetailDialog'
import { PatientDialog } from './PatientDialog'

export function PatientsView() {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const { isAdmin } = useAuth()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const searchPatient = useSearchPatient()

  const [patients, setPatients] = useState<Patient[]>([])
  const [patient, setPatient] = useState<Patient | null>(null)
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const [selectedPatientIdForDetail, setSelectedPatientIdForDetail] = useState<string | null>(null)
  const [patientOpen, setPatientOpen] = useState(false)
  const [patientDetailOpen, setPatientDetailOpen] = useState(false)

  const canManage = isAdmin
  const isLoading = searchPatient.isPending
  const displayPatients = patients ?? []

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  async function handleSearch() {
    if (!search) return

    setHasSearched(true)
    const res = await searchPatient.mutateAsync(search)

    if (res.source === 'his') {
      setPatient({
        id: '',
        hn: res.data.hn ?? '',
        bedId: '',
        doctorId: '',

        departmentId: '',
        departmentName: '',
        title: res.data.title ?? '',
        fullName: '',
        firstName: res.data.firstName ?? '',
        midName: '',
        lastName: res.data.lastName ?? '',
        sex: res.data.gender ?? '',
        birthDate: res.data.birthDate ?? '',
        bloodGroup: '',
        nationality: '',
        race: '',
        religion: '',
        job: '',
        cardId: res.data.nationalId ?? '',
        passport: '',
        phoneNumber: '',
        address: '',
        email: '',
        emergencyName: '',
        emergencyPhoneNumber: '',
        emergencyRelation: '',
        historyMedicine: '',
        note: '',
      })
      setSelectedPatientId(null)
      setSelectedPatientIdForDetail(null)
      setPatientOpen(true)
    } else {
      setPatients(res.data)
    }
  }

  return (
    <>
      <SectionCard
        title={t('patient.title')}
        right={
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                data-testid="patients-search-input"
                placeholder={t('common.placeholder.searchPatient')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
                className="h-8 w-[200px] bg-secondary/50 pl-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1.5 items-start justify-end">
              <Button
                onClick={() => {
                  handleSearch()
                }}
                data-testid="patients-search-button"
                className="h-8 text-xs"
                disabled={!search || isLoading}
              >
                {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                {t('common.search')}
              </Button>
            </div>
            {canManage && (
              <Button
                size="sm"
                className="h-8 text-xs"
                data-testid="patients-create-button"
                onClick={() => {
                  setPatient(null)
                  setSelectedPatientId(null)
                  setSelectedPatientIdForDetail(null)
                  setPatientOpen(true)
                }}
              >
                <Plus className="mr-1 size-3" />
                {t('common.patient')}
              </Button>
            )}
          </>
        }
      >
        <div className="rounded-lg border border-border/30 ">
          <Table data-testid="patients-table">
            <TableHeader>
              <TableRow className="border-border/30 hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground">{t('patient.hn')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('common.name')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('patient.sex')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('patient.birthdate')}</TableHead>
                <TableHead className="text-xs text-muted-foreground">{t('common.bed')}</TableHead>
                <TableHead className="sr-only text-xs"></TableHead>
                <TableHead className="sr-only text-xs"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : !hasSearched ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {t('patient.searchPatient')}
                  </TableCell>
                </TableRow>
              ) : displayPatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {t('patient.notFound')}
                  </TableCell>
                </TableRow>
              ) : (
                displayPatients.map((p: Patient) => {
                  return (
                    <TableRow
                      key={p.id}
                      data-patient-id={p.id}
                      data-testid={`patient-row-${p.id}`}
                      className="border-border/20 hover:bg-secondary/40"
                    >
                      <TableCell className="text-xs font-mono text-foreground">{p.hn}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.fullName}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.sex === 'Male'
                          ? t('patient.male')
                          : p.sex === 'Female'
                            ? t('patient.female')
                            : t('patient.unknown')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(p.birthDate, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.bedName ? (
                          <span className="flex items-center gap-1">
                            <Bed className="size-3" />
                            {p.bedName}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] text-primary hover:text-primary"
                          data-testid="patient-row-view"
                          onClick={() => {
                            setPatient(null)
                            setSelectedPatientId(null)
                            setSelectedPatientIdForDetail(p.id)
                            setPatientDetailOpen(true)
                          }}
                        >
                          <Eye className="mr-1 size-3" />
                          {t('common.view')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] text-primary hover:text-primary"
                          data-testid="patient-row-edit"
                          onClick={() => {
                            setPatient(null)
                            setSelectedPatientIdForDetail(null)
                            setSelectedPatientId(p.id)
                            setPatientOpen(true)
                          }}
                        >
                          <Edit2 className="mr-1 size-3" />
                          {t('common.edit')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
      <PatientDialog
        open={patientOpen}
        onOpenChange={setPatientOpen}
        hisPatient={patient}
        patientId={selectedPatientId}
        onSuccess={patient => {
          setSearch(patient.hn)
          handleSearch()
        }}
      />
      <PatientDetailDialog
        open={patientDetailOpen}
        onOpenChange={setPatientDetailOpen}
        patientId={selectedPatientIdForDetail}
      />
    </>
  )
}
